import { PathElement } from '../types/DynamicFormTypes';

export const cssprefix = 'tag__dynamic-form__';

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
type CheckTypeFnType = (arg0: any) => string;
export const checkType: CheckTypeFnType = (obj) => Object.prototype.toString.call(obj).slice(8, -1);

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
type IsFnType = (arg0: string) => (arg1: any) => boolean;
export const is: IsFnType = (type) => (obj) => typeof obj !== 'undefined' && obj !== null && checkType(obj) === type;

export type NestedPropertyType<T, K extends PropertyKey[]> = K extends [infer First, ...infer Rest]
	? First extends keyof T
		? Rest extends PropertyKey[]
			? NestedPropertyType<T[First], Rest>
			: T[First]
		: undefined
	: T;

export type PathFnType = <T, K extends PropertyKey[]>(keys: K, obj: T) => NestedPropertyType<T, K> | undefined;
export const path: PathFnType = <T, K extends PropertyKey[]>(keys: K, obj: T) =>
	keys.reduce(
		(acc: unknown, key: PropertyKey): unknown =>
			is('Object')(acc) || Array.isArray(acc) ? (acc as Record<PropertyKey, unknown>)[key] : undefined,
		obj as unknown
	) as NestedPropertyType<T, K> | undefined;

export type SelectPathsFnType = <T, K extends PropertyKey[][]>(
	keyLists: K,
	obj: T
) => {
	[P in keyof K]: NestedPropertyType<T, K[P]> | undefined;
};
export const selectPaths: SelectPathsFnType = <T, K extends PropertyKey[][]>(keyLists: K, obj: T) =>
	keyLists.map((keys) => path(keys, obj)) as { [P in keyof K]: NestedPropertyType<T, K[P]> | undefined };

export type DefaultToFnType = <T, U>(fallback: T, value: U | undefined) => T | U;
export const defaultTo: DefaultToFnType = (fallback, value) => (typeof value !== 'undefined' ? value : fallback);

export const pathOr = <T, U, K extends PropertyKey[]>(fallback: T, keys: K, obj: U): T | NestedPropertyType<U, K> =>
	defaultTo(fallback, path(keys, obj));

// Type to represent valid path keys (string | number)
// type PathKey = string | number;

// Type to represent the target object (can be array or object)
/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
type Target = Record<string, unknown> | unknown[];

/**
 * Sets a value at a path within an object or array
 * @param path Array of keys/indices representing the path
 * @param value Value to set at the path
 * @param obj Target object or array
 */
export const assocPath = <T extends Target, V>(path: PathElement[], value: V, target: T): T => {
	if (!path.length) {
		return target;
	}

	const [head, ...rest] = path;
	// Handle undefined target
	const safeTarget = target ?? (typeof head === 'number' ? [] : {});

	return Array.isArray(safeTarget)
		? // Handle array case
		  ([
				...safeTarget.slice(0, head as number),
				rest.length ? assocPath(rest, value, safeTarget[head as number] as Target) : value,
				...safeTarget.slice((head as number) + 1),
		  ] as T)
		: // Handle object case
		  Object.assign({}, safeTarget, {
				[head]: rest.length ? assocPath(rest, value, safeTarget[head] as Target) : value,
		  } as T);
};

export const isArraysEqual = (arr1: Array<unknown>, arr2: Array<unknown>): boolean => {
	if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false;
	if (arr1.length !== arr2.length) return false;

	return arr1.every((element, index) => {
		if (Array.isArray(element) && Array.isArray(arr2[index])) {
			return isArraysEqual(element, arr2[index] as Array<unknown>);
		}
		return element === arr2[index];
	});
};

// Generic flatten function that works with nested arrays
export const flatten = <T>(arr: T[][]): T[] => ([] as T[]).concat(...arr);

export function pipe<T>(fn1: (arg: T) => T, ...fns: Array<(arg: T) => T>): (arg: T) => T {
	return fns.reduce((prevFn, nextFn) => (value: T) => nextFn(prevFn(value)), fn1);
}

type OmitFnType = <T>(keyList: PropertyKey[], obj: Record<string, T>) => Record<string, T>;
export const omit: OmitFnType = (keyList = [], obj = {}) =>
	Object.keys(obj).reduce((acc, key) => (keyList.includes(key) ? acc : Object.assign(acc, { [key]: obj[key] })), {});

export type JoinFnType = <T>(arr: T[][], glue: string) => string;
export const join: JoinFnType = (arr = [], glue = ', ') => arr.join(glue);

type GroupByFnType = <T>(
	groupingFn: (item: T) => string,
	list: T[],
	initVal?: Record<string, T[]>
) => Record<string, T[]>;
export const groupBy: GroupByFnType = (groupingFn, list, initVal = {}) =>
	list.reduce((acc, item) => {
		const key = groupingFn(item);
		const lookupArr = acc[key] || [];
		return Object.assign(acc, { [key]: lookupArr.concat(item) });
	}, initVal);

type SortObjByFnType = <T>(srtFn: (a: string, b: string) => number) => (obj: Record<string, T>) => T[];

const sortObjBy: SortObjByFnType = (srtFn) => (obj) =>
	Object.keys(obj)
		.sort(srtFn)
		.map((key) => obj[key]);

export const sortGroupBy = <T>(
	grpfn: (item: T) => string,
	srtFn: (a: string | number, b: string | number) => number,
	arr: T[]
) => sortObjBy<T[]>(srtFn)(groupBy(grpfn, arr));

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
export const safeParseFloat = (val: any): number =>
	((parsedVal: number | typeof NaN) => (isNaN(parsedVal) ? 1 : parsedVal))(parseFloat(val as string));

/* eslint-disable-next-line  @typescript-eslint/no-explicit-any */
export const formatNumber = (val: any): number | string =>
	((parsedVal: number | typeof NaN) => (isNaN(parsedVal) ? '' : parsedVal))(parseFloat(val as string));

export const formatUSTel = (entry: string | number | undefined): string => {
	const numStr = entry ? `${entry}`.replace(/\D+/g, '') : '';
	if (numStr === '') {
		return numStr;
	} else {
		const [nums] = /\d{1,10}/.exec(numStr) ?? [];
		return [
			nums && nums.length > 3 ? `(${nums.substring(0, 3)}) ${nums.substring(3, 6)}` : nums,
			nums && nums.length > 6 ? `-${nums.substring(6, 10)}` : '',
		].join('');
	}
};

export const formatDateUTC = (entry: string | undefined): string => {
	const maybeDate = new Date(entry as string).toLocaleDateString('en-CA');
	return entry && (maybeDate === 'Invalid Date' || entry.includes('M') || entry.includes('D') || entry.includes('Y'))
		? entry
		: maybeDate;
};

export const redirectTo = (url?: string) => {
	if (url) {
		globalThis.location.href = url;
	}
};
